import {useMutation} from '@tanstack/react-query';

import {apiClient} from '../../../../utils/api-client';
import {isDocumentDraft} from '../../../../utils/document';
import {useSearchDocsCache} from '../sidebar/search-layout/hooks/use-edit-mode-search-query-cache';

import {useCategoriesInfiniteQueryCache} from './categories/use-categories-infinite-query-cache';
import {useDocumentQueryCache} from './use-document-query';
import {useOntologyDocsQueryCache} from './use-ontology-docs-query';
import {useUnpublishedDocsQueryCache} from './use-unpublished-docs-query';
import {useFetchWorkingCopyQueryCache} from './use-fetch-working-copy-query';

function discardWorkingCopyRequest(accession: string) {
    return apiClient.callApi({
        path: `editor-documents-service/api/documents/${accession}/wc`,
        method: 'DELETE'
    });
}

function useDiscardDraftInCache() {
    const documentQueryCache = useDocumentQueryCache();
    const searchDocumentsCache = useSearchDocsCache();
    const ontologyDocumentsQueryCache = useOntologyDocsQueryCache();
    const categoriesInfiniteQueryCache = useCategoriesInfiniteQueryCache();

    return (document: DraftDocument) => {
        documentQueryCache.remove(document.accession);

        searchDocumentsCache.deleteDocument(document.accession);
        ontologyDocumentsQueryCache.deleteDocument(document);

        if (document.type === 'BTB') {
            categoriesInfiniteQueryCache.updateDocumentsCount('root', -1);
        }
    };
}

function useDiscardWorkingCopyInCache() {
    const documentQueryCache = useDocumentQueryCache();
    const searchDocumentsCache = useSearchDocsCache();
    const ontologyDocsQueryCache = useOntologyDocsQueryCache();
    const categoriesInfiniteQueryCache = useCategoriesInfiniteQueryCache();

    return async (accession: string) => {
        searchDocumentsCache.clearCache();

        const nextDocument = await documentQueryCache.get(accession);

        if (nextDocument) {
            await categoriesInfiniteQueryCache.invalidate();
            await ontologyDocsQueryCache.invalidate(nextDocument.type);
        }
    };
}

export function useDiscardWorkingCopyMutation() {
    const workingCopyQueryCache = useFetchWorkingCopyQueryCache();
    const discardDraftInCache = useDiscardDraftInCache();
    const discardWorkingCopyInCache = useDiscardWorkingCopyInCache();

    const unpublishedDocsQueryCache = useUnpublishedDocsQueryCache();

    return useMutation({
        mutationFn: (document: EditModeDoc) => discardWorkingCopyRequest(document.accession),
        onSuccess: async (_, document) => {
            if (isDocumentDraft(document)) {
                discardDraftInCache(document);
            } else {
                await discardWorkingCopyInCache(document.accession);
            }

            workingCopyQueryCache.remove(document.accession);
            unpublishedDocsQueryCache.deleteDocument(document.accession);
        }
    });
}
